import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import styled from 'styled-components';

import 'video-react/dist/video-react.css';
import { Event, Tag, TagState, Tags } from '../../../interfaces';
import { useEventTags, useTagsQuery } from '../../../queries';
import { humanizeEnum, tagDescription } from '../../../utils';
import { FilterToggleWrapper, HelpIcon, Permission, SpaceBetween, Spacer, ToggleButton } from '../../atoms';
import { handleButtonClick, handleDropdownClick, handleTagClick } from '../../reviewGridMatchedEvents/utils';
import { Autocomplete } from './Autocomplete';
import { Question } from './Question';
import { useHasPermission, useAssetManagement } from '../../../hooks';
import { FleetUUIDs } from '../../../enums';
import { getTagLabel } from '../../../utils/tagIconsUtils';

interface Props {
  event?: Event;
  tags?: Tags;
  hideButtons?: boolean;
  onUserTagChange?: (tags: Tags) => void;
  onTagsLoad?: (tags: Tags) => void;
  withAutocomplete?: boolean;
}

const TagsContainer = styled.div<{ isAssetManagement: boolean }>`
  ${(props) =>
    props.isAssetManagement &&
    `
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  `}
`;

/**
 * The event tags component is used to display and edit tags for an event.
 *
 * @param props
 * @returns
 */

export function EventTags(props: Props) {
  const [tags, setTags] = useState<Tags>(props.tags || {});
  const [questions, setQuestions] = useState<Tag[]>([]);
  const canWrite = useHasPermission('events:write:tags');

  const isAssetManagement = useAssetManagement();
  // Tag definition IDs for SacRT asset management observations
  // These tags have their names hidden (set to empty string) to display as icon-only buttons
  const tagsDefinitionsIdForSacRT = [
    780, // 'Other'
    775, // 'Broken glass'
    778, // 'Garbage overflow'
    776, // 'Graffiti'
    777, // 'Lights out'
    772, // 'Sign missing/faded'
    774, // 'Pole is broken / missing'
    779, // 'Good condition'
    781, // 'Faded / missing advertisements'
    814, // 'Unattended Personal Property'
  ];

  const availableTags = useTagsQuery();
  const eventTags = useEventTags(props.event?.id);

  // TODO: Temporary solution for SacRT-specific tag name customization.
  // This should be replaced with a more generic tag management system that allows
  const filteredQuestions = useMemo(() => {
    if (!isAssetManagement) {
      return questions;
    }

    return (
      questions
        // don't show Sacramento Asset Description (probably to remove from dataBase later)
        .filter((q) => q.fleet_uuids?.includes(FleetUUIDs.SacRTBusStopShelter) && q.id !== 665)
        .map((q) => {
          // ID 632: Sacrt Bus Stop Rejection Reason → "Rejection Reason"
          if (q.id === 632) {
            return { ...q, name: 'Rejection Reason' };
          }

          // IDs in tagsDefinitionsIdForSacRT: Set name to empty string
          if (tagsDefinitionsIdForSacRT.includes(q.id)) {
            return { ...q, name: '' };
          }

          return q;
        })
        .sort((a, b) => {
          // Sort by variant name (for tags with empty names, use first enum variant name)
          const aName = a.name || a.enum_variants?.[0]?.variant_name || '';
          const bName = b.name || b.enum_variants?.[0]?.variant_name || '';
          return aName.localeCompare(bName);
        })
    );
  }, [questions, isAssetManagement]);

  // Filter tags to only show those whose tag definition ID exists in filteredQuestions
  const tagsToDisplay = useMemo(() => {
    const filteredQuestionIds = new Set(filteredQuestions.map((q) => q.id));
    const displayTags: Tags = {};

    Object.entries(tags).forEach(([key, value]) => {
      // Extract tag definition ID from key (format: "td:665")
      if (key.startsWith('td:')) {
        const tagDefinitionId = parseInt(key.split(':')[1], 10);

        // Only include tag if its definition ID exists in filteredQuestions
        if (filteredQuestionIds.has(tagDefinitionId)) {
          displayTags[key] = value;
        }
      } else {
        // Keep non-td tags as-is (if any exist)
        displayTags[key] = value;
      }
    });

    return displayTags;
  }, [tags, filteredQuestions]);

  useEffect(() => {
    if (availableTags.data) {
      const activeQuestions = availableTags.data.filter((x: Tag) => x.state === TagState.Active);
      setQuestions(activeQuestions);
    }
  }, [availableTags.data]);

  // Always synchronize with props.tags (even when undefined or empty)
  // This ensures tags are cleared when "Clear filters" button is clicked
  useEffect(() => {
    setTags(props.tags || {});
  }, [props.tags]);

  useEffect(() => {
    // Set default tags selection to whatever is backend response for that event
    if (eventTags.isLoading && !props.tags) {
      setTags({});
    }
    if (eventTags.data) {
      setTags(eventTags.data);
      props.onTagsLoad?.(eventTags.data);
      props.onUserTagChange?.(eventTags.data);
    }
  }, [eventTags.data, eventTags.isLoading]);

  return (
    <TagsContainer isAssetManagement={isAssetManagement}>
      {!props.hideButtons &&
        filteredQuestions
          .filter((q) => !q.is_rare)
          .map((question) => (
            <div key={question.id}>
              {question.name && !isAssetManagement && (
                <>
                  <SpaceBetween>
                    <label>{humanizeEnum(question.name)}</label>
                    {question.description && <HelpIcon info={question.description} />}
                  </SpaceBetween>

                  <Spacer half />
                </>
              )}

              <Question
                question={question}
                onTagClick={handleButtonClick}
                tags={tagsToDisplay}
                setTags={setTags}
                onUserTagChange={props.onUserTagChange}
                disabled={!canWrite}
                isAssetManagement={isAssetManagement}
              />

              {!isAssetManagement && <hr />}
            </div>
          ))}

      {props.withAutocomplete && !isAssetManagement && (
        <Permission permission="events:write:tags">
          <div>
            <Autocomplete
              onTagClick={handleDropdownClick}
              questions={filteredQuestions}
              tags={tagsToDisplay}
              setTags={setTags}
              onUserTagChange={props.onUserTagChange}
              dataTestId="autocompleteTags"
            />
            {availableTags.isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FilterToggleWrapper>
                {Object.entries(tagsToDisplay).map(([key, value]) => {
                  const label = isAssetManagement ? getTagLabel(value) : tagDescription(key, value, filteredQuestions);

                  return (
                    <ToggleButton
                      onClick={handleTagClick(key, tags, setTags, props.onUserTagChange)}
                      key={key + value}
                      disabled={!canWrite}
                    >
                      {label}
                    </ToggleButton>
                  );
                })}
              </FilterToggleWrapper>
            )}
          </div>
        </Permission>
      )}
    </TagsContainer>
  );
}
